import React, { useContext, useEffect, useState } from 'react';
import { Select, StatusIndicator } from '@amzn/awsui-components-core';
import { ContentWorkflow } from '@amzn/awscat-pattern-library-service-model';
import { BarRaiserStatusOptions, TechWriterStatusOptions } from '../utils/SelectableOptions';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';
import { getSelectOption } from '../../api/UtilsApi';
import { barRaiserStatusDisplayNames, techWriterStatusDisplayNames } from '../utils/DisplayNames';
import { ContentQueryContext } from '../content/table/ContentQueryContext';
import UserTitle from '../../model/UserTitle';
import { BarRaiserStatus } from '@amzn/awscat-pattern-library-service-model';

interface Props {
  workflow: ContentWorkflow;
  userTitle: UserTitle;
  forPublishedContents?: boolean;
}

const StatusSelect = ({ workflow, userTitle, forPublishedContents }: Props) => {
  const {
    contentId,
    publicationWorkflowId,
    draftWorkflowId,
    techWriterStatus,
    barRaiserStatus,
    publicationWorkflows,
  } = workflow;
  const [isLoading, setIsLoading] = useState(false);
  const [allOptions, setAllOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const { queryState, dispatchQueryState } = useContext(ContentQueryContext);
  const [isReadOnly, setIsReadOnly] = useState<boolean>(false);

  const mplServiceClient = MplServiceClientFactory.create();

  useEffect(() => {
    if (userTitle === UserTitle.Techwriter) {
      const techWriterSelectedStatus = techWriterStatus
        ? getSelectOption(techWriterStatus, techWriterStatusDisplayNames.get(techWriterStatus))
        : null;
      const allTechWriterStatusOptions = [
        { id: 'Empty', label: '<Clear status>', value: undefined },
        ...TechWriterStatusOptions,
      ];
      // @ts-expect-error TS2345 - strict migration
      setAllOptions(allTechWriterStatusOptions);
      // @ts-expect-error TS2345 - strict migration
      setSelectedOption(techWriterSelectedStatus);
    }
    if (userTitle === UserTitle.BarRaiser) {
      const publishedBarRaiserStatus =
        forPublishedContents && publicationWorkflows[0]?.barRaiserStatus
          ? getSelectOption(
              publicationWorkflows[0].barRaiserStatus,
              barRaiserStatusDisplayNames.get(publicationWorkflows[0].barRaiserStatus),
            )
          : undefined;
      const barRaiserSelectedStatus = barRaiserStatus
        ? getSelectOption(barRaiserStatus, barRaiserStatusDisplayNames.get(barRaiserStatus))
        : publishedBarRaiserStatus;

      // Only show Unpublish recommended option when content is already in Unpublished Recommended status
      const isUnpublishedRecommended =
        barRaiserSelectedStatus?.value === BarRaiserStatus.UnpublishingRecommended;
      // @ts-expect-error TS2345 - strict migration
      const options = BarRaiserStatusOptions(forPublishedContents && isUnpublishedRecommended);
      const allBarRaiserStatusOptions = [
        { id: 'Empty', label: '<Clear status>', value: undefined },
        ...options,
      ];
      // @ts-expect-error TS2345 - strict migration
      setAllOptions(allBarRaiserStatusOptions);
      // @ts-expect-error TS2345 - strict migration
      setSelectedOption(barRaiserSelectedStatus);
      setIsReadOnly(isUnpublishedRecommended);
    }
  }, [
    contentId,
    barRaiserStatus,
    techWriterStatus,
    forPublishedContents,
    publicationWorkflows,
    userTitle,
  ]);

  const updateStatus = async (updatedOption: any) => {
    setSelectedOption(updatedOption);
    setIsLoading(true);
    // @ts-expect-error TS18048 - strict migration
    const tableItem = queryState.queryResult.find((wf) => wf.contentId === contentId);
    try {
      if (userTitle === UserTitle.Techwriter) {
        if (updatedOption.value) {
          await mplServiceClient.updateTechWriterStatus(
            contentId,
            publicationWorkflowId,
            updatedOption.value,
          );
        } else {
          await mplServiceClient.deleteTechWriterStatus(contentId, publicationWorkflowId);
        }
        // @ts-expect-error TS18048 - strict migration
        tableItem.workflow.techWriterStatus = updatedOption.value;
      }
      if (userTitle === UserTitle.BarRaiser) {
        if (updatedOption.value) {
          await mplServiceClient.updateBarRaiserStatus(
            contentId,
            forPublishedContents ? publicationWorkflowId : draftWorkflowId,
            updatedOption.value,
          );
        } else {
          await mplServiceClient.deleteBarRaiserStatus(
            contentId,
            forPublishedContents ? publicationWorkflowId : draftWorkflowId,
          );
        }
        // @ts-expect-error TS18048 - strict migration
        tableItem.workflow.barRaiserStatus = updatedOption.value;
      }
      const modifiedQueryResult = [...queryState.queryResult];
      dispatchQueryState({ queryResult: modifiedQueryResult });
    } finally {
      setIsLoading(false);
    }
  };

  if (userTitle === UserTitle.Techwriter && !publicationWorkflowId) {
    return null;
  }
  if (userTitle === UserTitle.BarRaiser && !draftWorkflowId) {
    return null;
  }

  if (isLoading) {
    return <StatusIndicator type="loading">Updating status...</StatusIndicator>;
  } else {
    return (
      <Select
        selectedOption={selectedOption}
        onChange={({ detail }) => updateStatus(detail.selectedOption)}
        options={allOptions}
        readOnly={isReadOnly}
        selectedAriaLabel="Select"
        expandToViewport
        ariaRequired
      />
    );
  }
};

export default StatusSelect;
